// Share links (v4 ticket V4-SH, spec-v4-director-plus.md `share_link` / `revoke_link`): a private watch page for one
// finished version at /s/<token>. No account to watch, never indexed; viewers leave comments at a moment and they
// reach the owner as review notes. This file is the data side (types, parsing, pure helpers, the http calls; the mock
// under VITE_MOCK=1): the owner's share sheet is components/ShareSheet.tsx, the watch page screens/Share.tsx, the
// admin queue screens/AdminReports.tsx. Unit-tested in share.test.ts.
import { ApiError, call } from "./api";

// ------------------------------------------------------------------ types

export type ShareState = "live" | "expired" | "off";
export type ShareLink = {
  id: string;
  projectId: string;
  version: number | null;
  url: string;
  state: ShareState;
  hasPassword: boolean;
  commentsOn: boolean;
  expiresAt: string | null;
  revokedBy: "owner" | "admin" | null;
  views: number;
  comments: number;
  createdAt: string;
};
export type ShareComment = { id: string; name: string; body: string; atS: number; createdAt: string };
/** A comment as the owner sees it: which link and version, and the review note it became. */
export type OwnerComment = ShareComment & { linkId: string; noteId: string | null; version: number | null };
export type ShareList = { links: ShareLink[]; comments: OwnerComment[] };
export type NewShare = { expiresInDays: number | null; password?: string; comments?: boolean };

export type SharePage =
  | { state: "off" | "expired" | "password" | "missing" }
  | { state: "ok"; title: string; version: number | null; durationS: number; aspect: string | null; videoUrl: string; posterUrl: string | null; commentsOn: boolean; comments: ShareComment[]; expiresAt: string | null };

export const REPORT_REASONS = [
  { id: "illegal", label: "Illegal content" },
  { id: "sexual", label: "Sexual content or nudity" },
  { id: "violence", label: "Violence or threats" },
  { id: "harassment", label: "Harassment or hate" },
  { id: "copyright", label: "It uses my work (copyright)" },
  { id: "privacy", label: "My private information or likeness" },
  { id: "spam", label: "Spam" },
  { id: "other", label: "Something else" },
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number]["id"];
export type ReportInput = { reason: ReportReason; details?: string; email?: string; commentId?: string };

export type ReportAction = "dismiss" | "turn_off_link" | "remove_comment";
export type ShareReport = {
  id: string;
  reason: string;
  reasonLabel: string;
  details: string;
  email: string | null;
  status: "open" | "dismissed" | "actioned";
  action: ReportAction | null;
  createdAt: string;
  link: { id: string; url: string; state: ShareState; title: string; version: number | null; ownerEmail: string; videoUrl: string | null };
  comment: { id: string; name: string; body: string; atS: number } | null;
};

/** The expiry choices in the share sheet (days; null = never). 30 days is the default (as the Director's). */
export const EXPIRY_CHOICES: { days: number | null; label: string }[] = [
  { days: 1, label: "1 day" },
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: null, label: "Never" },
];
export const DEFAULT_EXPIRY = 30;
export const NAME_MAX = 60;
export const COMMENT_MAX = 1000;

// ------------------------------------------------------------------ parsing (never trust the wire)

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === "string" ? v : "");
const num = (v: unknown, d = 0): number => (typeof v === "number" && Number.isFinite(v) ? v : d);
const numOrNull = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const strOrNull = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
/** Same-origin paths or http(s) links only: never a javascript: URL in a src. */
export const safeUrl = (v: unknown): string => {
  const s = str(v);
  return /^\/(?!\/)/.test(s) || /^https?:\/\//i.test(s) || /^data:image\//.test(s) || /^blob:/.test(s) ? s : "";
};
const STATES: ShareState[] = ["live", "expired", "off"];

export function normLink(v: unknown): ShareLink | null {
  if (!isObj(v) || typeof v.id !== "string" || typeof v.url !== "string") return null;
  return {
    id: v.id,
    projectId: str(v.projectId),
    version: numOrNull(v.version),
    url: v.url,
    state: STATES.includes(v.state as ShareState) ? (v.state as ShareState) : "off",
    hasPassword: v.hasPassword === true,
    commentsOn: v.commentsOn !== false,
    expiresAt: strOrNull(v.expiresAt),
    revokedBy: v.revokedBy === "owner" || v.revokedBy === "admin" ? v.revokedBy : null,
    views: num(v.views),
    comments: num(v.comments),
    createdAt: str(v.createdAt),
  };
}

export function normComment(v: unknown): ShareComment | null {
  if (!isObj(v) || typeof v.id !== "string") return null;
  return { id: v.id, name: str(v.name), body: str(v.body), atS: Math.max(0, num(v.atS)), createdAt: str(v.createdAt) };
}

export function normShareList(v: unknown): ShareList {
  const o = isObj(v) ? v : {};
  const links = (Array.isArray(o.links) ? o.links : []).map(normLink).filter((x): x is ShareLink => !!x);
  const comments = (Array.isArray(o.comments) ? o.comments : [])
    .map((c) => {
      const b = normComment(c);
      return b && isObj(c) ? { ...b, linkId: str(c.linkId), noteId: strOrNull(c.noteId), version: numOrNull(c.version) } : null;
    })
    .filter((x): x is OwnerComment => !!x);
  return { links, comments };
}

export function normSharePage(v: unknown): SharePage {
  if (!isObj(v)) return { state: "missing" };
  if (v.state === "off" || v.state === "expired" || v.state === "password") return { state: v.state };
  if (v.state !== "ok") return { state: "missing" };
  const videoUrl = safeUrl(v.videoUrl);
  if (!videoUrl) return { state: "off" };
  return {
    state: "ok",
    title: str(v.title) || "A video",
    version: numOrNull(v.version),
    durationS: num(v.durationS),
    aspect: strOrNull(v.aspect),
    videoUrl,
    posterUrl: safeUrl(v.posterUrl) || null,
    commentsOn: v.commentsOn !== false,
    comments: (Array.isArray(v.comments) ? v.comments : []).map(normComment).filter((x): x is ShareComment => !!x),
    expiresAt: strOrNull(v.expiresAt),
  };
}

export function normReport(v: unknown): ShareReport | null {
  if (!isObj(v) || typeof v.id !== "string" || !isObj(v.link)) return null;
  const l = v.link;
  const c = isObj(v.comment) ? v.comment : null;
  return {
    id: v.id,
    reason: str(v.reason),
    reasonLabel: str(v.reasonLabel) || str(v.reason),
    details: str(v.details),
    email: strOrNull(v.email),
    status: v.status === "dismissed" || v.status === "actioned" ? v.status : "open",
    action: v.action === "dismiss" || v.action === "turn_off_link" || v.action === "remove_comment" ? v.action : null,
    createdAt: str(v.createdAt),
    link: { id: str(l.id), url: str(l.url), state: STATES.includes(l.state as ShareState) ? (l.state as ShareState) : "off", title: str(l.title), version: numOrNull(l.version), ownerEmail: str(l.ownerEmail), videoUrl: safeUrl(l.videoUrl) || null },
    comment: c ? { id: str(c.id), name: str(c.name), body: str(c.body), atS: num(c.atS) } : null,
  };
}

// ------------------------------------------------------------------ pure helpers

/** "/s/<token>" -> the token; anything else -> null. */
export function sharePath(path: string): string | null {
  const m = path.replace(/\/+$/, "").match(/^\/s\/([A-Za-z0-9_-]{16,64})$/);
  return m ? m[1]! : null;
}

/** 0:07, 1:05, 1:02:03 */
export function fmtAt(s: number): string {
  const t = Math.max(0, Math.floor(s));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = String(t % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

/** "Expires in 6 days", "Expires in 3 hours", "Doesn't expire", "Expired", "Turned off". */
export function linkStatus(l: Pick<ShareLink, "state" | "expiresAt" | "revokedBy">, now = Date.now()): string {
  if (l.state === "off") return l.revokedBy === "admin" ? "Turned off by CAO Studio" : "Turned off";
  if (l.state === "expired") return "Expired";
  if (!l.expiresAt) return "Doesn't expire";
  const ms = Date.parse(l.expiresAt) - now;
  if (ms <= 0) return "Expired";
  const h = Math.round(ms / 3600_000);
  if (h < 36) return `Expires in ${Math.max(1, h)} hour${h <= 1 ? "" : "s"}`;
  return `Expires in ${Math.round(h / 24)} days`;
}

/** "3 views · 2 comments · password" for a link row. */
export function linkMeta(l: Pick<ShareLink, "views" | "comments" | "hasPassword" | "commentsOn">): string {
  const n = (k: number, w: string) => `${k} ${w}${k === 1 ? "" : "s"}`;
  return [n(l.views, "view"), l.commentsOn ? n(l.comments, "comment") : "comments off", l.hasPassword ? "password" : ""].filter(Boolean).join(" · ");
}

/** The links of one version, live first then newest. */
export function linksFor(list: ShareList, version: number): ShareLink[] {
  const rank = (s: ShareState) => (s === "live" ? 0 : 1);
  return list.links.filter((l) => l.version === version).sort((a, b) => rank(a.state) - rank(b.state) || b.createdAt.localeCompare(a.createdAt));
}

/** Comments sorted by moment, then when they were left. */
export const byMoment = <T extends ShareComment>(cs: T[]): T[] => [...cs].sort((a, b) => a.atS - b.atS || a.createdAt.localeCompare(b.createdAt));

// a page's password key and the viewer's name stay on this device (sessionStorage / localStorage), best-effort
const store = (kind: "session" | "local") => {
  try {
    return kind === "session" ? globalThis.sessionStorage : globalThis.localStorage;
  } catch {
    return undefined;
  }
};
export function savedKey(token: string): string | null {
  try {
    return store("session")?.getItem(`cao-share-key:${token}`) ?? null;
  } catch {
    return null;
  }
}
export function saveKey(token: string, key: string | null) {
  try {
    if (key) store("session")?.setItem(`cao-share-key:${token}`, key);
    else store("session")?.removeItem(`cao-share-key:${token}`);
  } catch {
    /* private mode */
  }
}
export function savedName(): string {
  try {
    return store("local")?.getItem("cao-share-name") ?? "";
  } catch {
    return "";
  }
}
export function saveName(name: string) {
  try {
    store("local")?.setItem("cao-share-name", name.slice(0, NAME_MAX));
  } catch {
    /* private mode */
  }
}

// ------------------------------------------------------------------ the calls

export type ShareSource = {
  list(projectId: string, version?: number | null): Promise<ShareList>;
  create(projectId: string, version: number, s: NewShare): Promise<ShareLink>;
  revoke(linkId: string): Promise<ShareLink>;
  deleteComment(commentId: string): Promise<void>;
  page(token: string, key: string | null, count?: boolean): Promise<SharePage>;
  unlock(token: string, password: string): Promise<string>;
  comment(token: string, key: string | null, c: { name: string; body: string; atS: number }): Promise<ShareComment>;
  report(token: string, r: ReportInput): Promise<void>;
  reports(all?: boolean): Promise<ShareReport[]>;
  act(reportId: string, action: ReportAction): Promise<ShareReport>;
};

/** The public endpoints: no cookie, no token (a signed-in owner opening their own link is just a viewer). */
async function pub<T>(method: string, path: string, key: string | null, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (key) headers["x-share-key"] = key;
  const r = await fetch(`/api/share/${path}`, { method, credentials: "omit", headers, body: body === undefined ? undefined : JSON.stringify(body), referrerPolicy: "no-referrer" });
  const text = await r.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }
  if (!r.ok) {
    const o = isObj(data) ? data : {};
    throw new ApiError(str(o.error) || "Something went wrong. Try again.", r.status, typeof o.code === "string" ? o.code : null);
  }
  return data as T;
}

export const httpShare: ShareSource = {
  async list(projectId, version) {
    return normShareList(await call("GET", `projects/${encodeURIComponent(projectId)}/shares${version ? `?version=${version}` : ""}`));
  },
  async create(projectId, version, s) {
    const r = await call<{ link: unknown }>("POST", `projects/${encodeURIComponent(projectId)}/versions/${version}/shares`, { expires_in_days: s.expiresInDays, password: s.password || null, comments: s.comments ?? true });
    const l = normLink(r.link);
    if (!l) throw new Error("The link didn't come back.");
    return l;
  },
  async revoke(linkId) {
    const l = normLink((await call<{ link: unknown }>("POST", `shares/${encodeURIComponent(linkId)}/revoke`)).link);
    if (!l) throw new Error("The link didn't come back.");
    return l;
  },
  async deleteComment(id) {
    await call("DELETE", `share-comments/${encodeURIComponent(id)}`);
  },
  async page(token, key, count = true) {
    try {
      return normSharePage(await pub("GET", `${encodeURIComponent(token)}${count ? "" : "?count=0"}`, key));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return { state: "missing" };
      throw e;
    }
  },
  async unlock(token, password) {
    return str((await pub<{ key?: unknown }>("POST", `${encodeURIComponent(token)}/unlock`, null, { password })).key);
  },
  async comment(token, key, c) {
    const r = normComment((await pub<{ comment: unknown }>("POST", `${encodeURIComponent(token)}/comments`, key, { name: c.name, body: c.body, at_s: c.atS })).comment);
    if (!r) throw new Error("The comment didn't come back.");
    return r;
  },
  async report(token, r) {
    await pub("POST", `${encodeURIComponent(token)}/report`, null, { reason: r.reason, details: r.details ?? "", ...(r.email ? { email: r.email } : {}), ...(r.commentId ? { comment_id: r.commentId } : {}) });
  },
  async reports(all = false) {
    const r = await call<{ reports?: unknown[] }>("GET", `admin/share-reports${all ? "?status=all" : ""}`);
    return (r.reports ?? []).map(normReport).filter((x): x is ShareReport => !!x);
  },
  async act(id, action) {
    const r = normReport((await call<{ report: unknown }>("POST", `admin/share-reports/${encodeURIComponent(id)}`, { action })).report);
    if (!r) throw new Error("The report didn't come back.");
    return r;
  },
};

/** The http calls, or the in-memory ones under VITE_MOCK=1 (src/mock/share.ts). */
export async function shareSource(): Promise<ShareSource> {
  if (import.meta.env?.VITE_MOCK === "1") return (await import("./mock/share")).mockShare;
  return httpShare;
}

// ------------------------------------------------------------------ open the owner's sheet from anywhere

export type ShareTarget = { projectId: string; version: number; title?: string; linkId?: string };
let target: ShareTarget | null = null;
const subs = new Set<() => void>();
export const shareTarget = () => target;
export function subscribeShare(f: () => void) {
  subs.add(f);
  return () => void subs.delete(f);
}
/** Opens the share sheet for a version (the Versions list's share button, or the Director's `share_sheet`). */
export function openShare(t: ShareTarget | null) {
  target = t;
  subs.forEach((f) => f());
}
